import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { auditActionDetail } from "@/lib/s12f2";   // Step 12 add-on G1 / G3 / G4: the detail of this add-on's rows
import { downloadJobFile } from "@/lib/jobFile";
/* eslint react-hooks/exhaustive-deps: 0 */
import SignoffsTab from "@/components/SignoffsTab";   // Batch 11 · A9
import AuditTrailPager, { W13_PAGE_SIZE } from "@/components/w13/AuditTrailPager";   // Step 13 · P3 · S5: the trail is paged
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";   // Stage 2 "Dashboard" · 2.2: /audit-trail?tab=signoffs from the Engagements tab
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Trash2, FileDown, CalendarIcon } from "lucide-react";
import { AiLogTab } from "@/components/AiLogTab";
import {
  actionLabel, roleLabel, resolveEntityLabel, entityDisplay,
} from "@/lib/w13Labels";

// K6 — shared LTR-isolated date formatter. Local copies removed
// (were: ClearanceGate / FileAssembly / AuditTrail).
import { fmtDate, fmtDateTime } from "@/lib/dt";
import { askPrompt } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language
function ltr(s) { return `\u2068${s}\u2069`; }
function parseYmd(s){if(!s)return null;const m=/^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));if(!m)return null;return new Date(Number(m[1]),Number(m[2])-1,Number(m[3]));}
function toYmd(d){if(!d)return"";const y=d.getFullYear();const m=String(d.getMonth()+1).padStart(2,"0");const dd=String(d.getDate()).padStart(2,"0");return `${y}-${m}-${dd}`;}

function DateField({ value, onCommit, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const selected = parseYmd(value);
  const label = value ? fmtDate(value) : (placeholder || "—");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm"
                className="h-9 w-full justify-start gap-1 px-2 font-mono text-[11px]"
                data-testid={testid}>
          <CalendarIcon className="h-3 w-3" />
          <span dir="ltr">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected || undefined}
                    onSelect={(d) => { setOpen(false); onCommit(toYmd(d) || ""); }}
                    initialFocus />
        {value && (
          <div className="p-2 border-t">
            <Button variant="ghost" size="sm" className="w-full text-[11px]"
                    onClick={() => { setOpen(false); onCommit(""); }}
                    data-testid={`${testid}-clear`}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function AuditTrailPage(){
  const { current, currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  // L3 — engagement type drives role-label mapping on the W13 page.
  const isReview = (current?.engagement_type || "audit") === "review";
  const [state, setState] = useState(null);
  const [deletions, setDeletions] = useState(null);
  // Step 11d · add-on: at, user, action, entity (+ the delete column a partner sees: its own key)
  const trailCols = useResizableColumns({ storageKey: state?.can_delete ? "audit-trail-del" : "audit-trail",
    defaults: state?.can_delete ? [150, 200, 260, 360, 70] : [150, 200, 260, 360], mins: [110, 120, 140, 160, 56] });
  const delCols = useResizableColumns({ storageKey: "audit-trail-deletions", defaults: [150, 180, 260, 420], mins: [110, 110, 140, 160] });
  const [formsIndex, setFormsIndex] = useState({});
  // Step 12-F3 · G8: /audit-trail?action=<action> (Verify Signed Bundle's "Audit Trail" link: ?action=signed_off) opens the trail
  // filtered to that action — before, the parameter was not read and the whole trail showed
  const [f, setFilters] = useState(() => ({ user_id:"", action: new URLSearchParams(window.location.search).get("action") || "", ref:"", from_date:"", to_date:"", search:"" }));
  // Stage 2 "Dashboard" · 2.2: the Engagements tab's Sign-offs cell deep-links to /audit-trail?tab=signoffs
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (["events", "signoffs", "ailog"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "events"));

  const [page, setPage] = useState(1);   // Step 13 · P3 · S5: one page of the filtered trail (filters / search on the server)
  const setF = (next) => { setFilters(next); setPage(1); };   // a new filter or search starts again from the first page
  const reload = async () => {
    if (!currentEngId) return;
    const q = new URLSearchParams({ ...f, page: String(page), page_size: String(W13_PAGE_SIZE) }).toString();
    const r = await api.get(`/engagements/${currentEngId}/w13?${q}`, { _silent: true });
    setState(r.data);
    if (r.data?.is_partner_view) {
      try { const dr = await api.get(`/engagements/${currentEngId}/w13/deletions`, { _silent: true }); setDeletions(dr.data); } catch(_){}
    }
  };
  useEffect(() => { setPage(1); }, [currentEngId]);
  useEffect(() => { reload(); }, [currentEngId, f, page]);

  // V3 — fetch the forms library so form / engagement_forms rows show
  // the library title (in the current screen language) instead of the
  // raw form_id slug.
  useEffect(() => {
    if (!currentEngId) return;
    api.get(`/forms/library`, { params: { engagement_id: currentEngId },
                                 _silent: true })
       .then(r => {
         const idx = {};
         for (const f of (r.data?.forms || [])) {
           if (f?.formId) idx[f.formId] = f;
         }
         setFormsIndex(idx);
       })
       .catch(() => setFormsIndex({}));
  }, [currentEngId]);

  const del = async (id) => {
    const reason = await askPrompt(t("w13.reasonPrompt"));
    if (!reason) return;
    try { await api.post(`/engagements/${currentEngId}/w13/entries/${id}/delete`, { reason }, { _silent: true });
      await reload(); toast.success(t("w13.deletedOk")); }
    catch(e){ toast.error(toErrMsg(e, "Delete failed")); }
  };
  const doExport = async (fmt) => {
    try {
      const r = await api.post(`/engagements/${currentEngId}/w13/export`, { format: fmt, filters: f }, { _silent: true });
      const jid = r.data?.job_id;
      // Batch 25 · B3 (M19): the finished export is downloaded (before, the screen only said "exported" — nothing was saved)
      for (let i=0;i<40;i++){await new Promise(rr=>setTimeout(rr,400));const j=await api.get(`/jobs/${jid}`,{_silent:true});
        if(j.data?.status==="done"){await downloadJobFile(jid, j.data?.result?.filename || `audit_trail.${fmt}`);toast.success(t("w13.exportedOk").replace("{n}", ltr(String(j.data?.result?.rows||0))));break;}
        if(j.data?.status==="failed"){toast.error(jobErrorText(j.data, lang, t, "Export failed"));break;}}
    } catch(e){ toast.error(toErrMsg(e, "Export failed")); }
  };

  const users = state?.users_index || [];
  const actions = state?.actions_index || [];

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w13-loading">{t("common.loading")}</div>;

  const ret = state.retention || {};
  const entityOpts = { formsIndex, lang, periodStart: current?.period_start_effective };

  return (
    <div className="space-y-6" data-testid="w13-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w13-title">{t("w13.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("w13.subtitle")}</p>
      </div>
      <div className="flex gap-1 border-b text-sm" data-testid="w13-tabs">
        {["events", "signoffs", "ailog"].map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={k === "ailog" ? "ailog-tab-btn" : (k === "signoffs" ? "w13-signoffs-tab-btn" : "w13-events-tab-btn")}
                  className={`-mb-px border-b-2 px-3 py-1.5 ${tab === k ? "border-slate-900 font-medium" : "border-transparent text-muted-foreground"}`}>
            {k === "ailog" ? t("ailog.tab") : (k === "signoffs" ? t("itemSignoff.tabTitle") : t("w13.title"))}
          </button>
        ))}
      </div>
      {tab === "ailog" && <AiLogTab t={t} lang={lang} engId={currentEngId} />}
      {tab === "signoffs" && <SignoffsTab t={t} engId={currentEngId} />}   {/* Batch 11 · A9 (D11) */}
      {tab === "events" && (<>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w13.retention")}</CardTitle></CardHeader>
        <CardContent className="text-[12px]" data-testid="w13-retention">
          {ret.is_set
            ? <span>{t("w13.retentionYears").replace("{n}", ltr(String(ret.years||"")))} · {ret.note||""}</span>
            : <span className="inline-flex items-center gap-1 text-amber-900"><AlertCircle className="h-3 w-3" />{t("w13.retentionNotSet")}</span>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w13.filters")}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-6">
          <Select value={f.user_id || "__all__"} onValueChange={v => setF({...f, user_id: v==="__all__"?"":v})}>
            <SelectTrigger className="h-9 text-[12px]" data-testid="w13-f-user">
              <SelectValue placeholder={t("w13.f.user")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("w13.f.userAll")}</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={f.action || "__all__"} onValueChange={v => setF({...f, action: v==="__all__"?"":v})}>
            <SelectTrigger className="h-9 text-[12px]" data-testid="w13-f-action">
              <SelectValue placeholder={t("w13.f.action")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("w13.f.actionAll")}</SelectItem>
              {actions.map(a => (
                <SelectItem key={a} value={a}>{actionLabel(t, a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder={t("w13.f.ref")} value={f.ref} onChange={e=>setF({...f,ref:e.target.value})} data-testid="w13-f-ref" className="h-9 text-[12px]" />
          <DateField value={f.from_date} onCommit={v=>setF({...f,from_date:v})} testid="w13-f-from" placeholder={t("w13.f.from")} />
          <DateField value={f.to_date}   onCommit={v=>setF({...f,to_date:v})}   testid="w13-f-to"   placeholder={t("w13.f.to")} />
          <Input placeholder={t("w13.f.search")} value={f.search} onChange={e=>setF({...f,search:e.target.value})} data-testid="w13-f-search" className="h-9 text-[12px]" />
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <div className="text-[11px] text-muted-foreground" data-testid="w13-count">{t("w13.rowCount").replace("{n}", ltr(String(state.count)))}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={()=>doExport("xlsx")} data-testid="w13-export-xlsx"><FileDown className="h-3 w-3 me-1"/>{t("w13.exportXlsx")}</Button>
          <Button variant="outline" size="sm" onClick={()=>doExport("csv")} data-testid="w13-export-csv"><FileDown className="h-3 w-3 me-1"/>{t("w13.exportCsv")}</Button>
        </div>
      </div>

      <AuditTrailPager t={t} page={state.page || 1} pages={state.pages || 1} count={state.count} pageSize={state.page_size || W13_PAGE_SIZE} onPage={setPage} />
      <div className="text-[11px] text-muted-foreground" data-testid="w13-export-full">{t("w13Pager.exportFull")}</div>
      <Card><CardContent className="p-0">
        <div className="overflow-auto rounded border">
          <ResizableTable cols={trailCols} className="w-full text-[12px]" data-testid="w13-table">
            <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground">
              <tr>
                <ResizableTh index={0} className="px-2 py-1.5 text-start">{t("w13.h.at")}</ResizableTh>
                <ResizableTh index={1} className="px-2 py-1.5 text-start">{t("w13.h.user")}</ResizableTh>
                <ResizableTh index={2} className="px-2 py-1.5 text-start">{t("w13.h.action")}</ResizableTh>
                <ResizableTh index={3} className="px-2 py-1.5 text-start">{t("w13.h.entity")}</ResizableTh>
                {state.can_delete && <ResizableTh index={4} className="px-2 py-1.5 text-start w-16"></ResizableTh>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {(state.rows||[]).map(r => {
                const roleTxt = roleLabel(t, r.user_role, { isReview });
                // W2 (Round W) — some legacy rows stored the role
                // parenthetical inside `user_name` ("Adil Tenant
                // (Practitioner)"). Strip a trailing "(…)" from the
                // display name before appending the current-language
                // role label so the cell doesn't repeat itself. This
                // is a DISPLAY-only strip; the stored row is not
                // modified (tamper evidence — R6/R7).
                const rawName = (r.user_name || "").replace(/\s*\([^()]*\)\s*$/, "").trim();
                const userCell = rawName
                  ? (roleTxt ? `${rawName} (${roleTxt})` : rawName)
                  : (roleTxt || "—");
                return (
                <tr key={r.id} data-testid={`w13-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-mono text-[10px]"><bdi>{fmtDateTime(r.created_at) || "—"}</bdi></td>
                  <td className="px-2 py-1.5" data-testid={`w13-user-${r.id}`}>{userCell}</td>
                  <td className="px-2 py-1.5">{actionLabel(t, r.action)}{auditActionDetail(t, r) ? ` ${auditActionDetail(t, r)}` : ""}</td>
                  <td className="px-2 py-1.5">{resolveEntityLabel(t, r, state.entity_lookup || {}, { ...entityOpts, isReview })}</td>
                  {state.can_delete && !state.locked && <td className="px-2 py-1.5">{r.action !== "audit_row_deleted" && !r.undeletable && <Button variant="ghost" size="sm" onClick={()=>del(r.id)} data-testid={`w13-del-${r.id}`}><Trash2 className="h-3 w-3 text-rose-700" /></Button>}</td>}
                </tr>
              );})}
              {(!state.rows || state.rows.length===0) && (
                <tr><td className="px-2 py-2 text-muted-foreground" colSpan={state.can_delete?5:4}>{t("w13.noRows")}</td></tr>
              )}
            </tbody>
          </ResizableTable>
        </div>
      </CardContent></Card>

      {state.is_partner_view && deletions && deletions.count>0 && (
        <Card>
          <CardHeader><CardTitle className="text-base text-rose-900 normal-case">{t("w13.deletionsTitle")}</CardTitle></CardHeader>
          <CardContent>
            <ResizableTable cols={delCols} wrap className="w-full text-[12px]" data-testid="w13-deletions-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground"><tr>
                <ResizableTh index={0} className="px-2 py-1.5 text-start">{t("w13.h.deletedAt")}</ResizableTh>
                <ResizableTh index={1} className="px-2 py-1.5 text-start">{t("w13.h.by")}</ResizableTh>
                <ResizableTh index={2} className="px-2 py-1.5 text-start">{t("w13.h.reason")}</ResizableTh>
                <ResizableTh index={3} className="px-2 py-1.5 text-start">{t("w13.h.entrySnap")}</ResizableTh>
              </tr></thead>
              <tbody className="divide-y">
                {deletions.rows.map(d => (
                  <tr key={d.id}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{fmtDate(d.deleted_at) || "—"}</td>
                    <td className="px-2 py-1.5">{d.deleted_by_name||"—"} ({roleLabel(t, d.deleted_by_role, { isReview })||"—"})</td>
                    <td className="px-2 py-1.5">{d.reason}</td>
                    <td className="px-2 py-1.5">{entityDisplay(t, (d.entry_snapshot||{}), { ...entityOpts, isReview })} · {actionLabel(t, (d.entry_snapshot||{}).action||"")}</td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </CardContent>
        </Card>
      )}
      </>)}
    </div>
  );
}
